/**
 * Modal Titlebar Drag Auto
 *
 * Automatically discovers draggable modal titlebars and enables drag behavior.
 */

import { Roles } from "../constants/roles";
import { enableModalTitlebarDrag } from "./modal-titlebar-drag";
import { FormBase } from "./form-base-form";

const ELEMENT_MODAL_HEADER_SELECTOR = `[data-role="${Roles.ELEMENT_MODAL_HEADER}"]`;
const CATEGORY_REVIEW_EDITOR_MODAL_SELECTOR = `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"]`;
const CATEGORY_REVIEW_EDITOR_WINDOW_SELECTOR = `[data-role~="${Roles.CATEGORY_REVIEW_EDITOR_WINDOW}"]`;
const SEARCH_MENU_MODAL_SELECTOR = `[data-role="${Roles.SEARCH_MENU_CONTAINER}"], [data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"]`;
const SEARCH_MODAL_SELECTOR = `[data-role="${Roles.SEARCH_CATEGORY_MODAL}"], [data-role="${Roles.SEARCH_BRAND_MODAL}"], [data-role="${Roles.SEARCH_USER_MODAL}"]`;
const ELEMENT_MODAL_SELECTOR = `[data-role="${Roles.ELEMENT_MODAL}"]`;

class ModalTitlebarDragAutoFormBase extends FormBase {
    public getClosest(root: Event | EventTarget | Element | null | undefined, selector: string): HTMLElement | null {
        return this.closest(root, selector, HTMLElement);
    }

    public getHeaders(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ELEMENT_MODAL_HEADER_SELECTOR, HTMLElement);
    }

    public hasHeader(root: ParentNode): boolean {
        return this.queryElement(root, ELEMENT_MODAL_HEADER_SELECTOR, HTMLElement) instanceof HTMLElement;
    }
}

const modalTitlebarDragAutoFormBase = new ModalTitlebarDragAutoFormBase();

function isVisible(element: HTMLElement): boolean {
    try {
        const view = element.ownerDocument?.defaultView;
        const cs = view?.getComputedStyle ? view.getComputedStyle(element) : null;
        if (!cs) return true;
        if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
    } catch {
        // ignore
    }

    return Boolean(element.getClientRects().length);
}

function resolveDraggableContainer(handleEl: HTMLElement): HTMLElement | null {
    const categoryReviewAddWindow = modalTitlebarDragAutoFormBase.getClosest(
        handleEl,
        CATEGORY_REVIEW_EDITOR_WINDOW_SELECTOR,
    );
    if (categoryReviewAddWindow instanceof HTMLElement) return categoryReviewAddWindow;

    const categoryReviewEditor = modalTitlebarDragAutoFormBase.getClosest(
        handleEl,
        CATEGORY_REVIEW_EDITOR_MODAL_SELECTOR,
    );
    if (categoryReviewEditor instanceof HTMLElement) return categoryReviewEditor;

    const searchMenu = modalTitlebarDragAutoFormBase.getClosest(handleEl, SEARCH_MENU_MODAL_SELECTOR);
    if (searchMenu instanceof HTMLElement) return searchMenu;

    const notificationsPopover = modalTitlebarDragAutoFormBase.getClosest(handleEl, ".navbar-notifications-popover");
    if (notificationsPopover instanceof HTMLElement) return notificationsPopover;

    const modalSearch = modalTitlebarDragAutoFormBase.getClosest(handleEl, SEARCH_MODAL_SELECTOR);
    if (modalSearch instanceof HTMLElement) return modalSearch;

    const avatarDialog = modalTitlebarDragAutoFormBase.getClosest(handleEl, ".avatar-modal-dialog");
    if (avatarDialog instanceof HTMLElement) return avatarDialog;

    const elementModal = modalTitlebarDragAutoFormBase.getClosest(handleEl, ELEMENT_MODAL_SELECTOR);
    if (elementModal instanceof HTMLElement) return elementModal;

    const dialog = modalTitlebarDragAutoFormBase.getClosest(handleEl, ".modal-dialog");
    if (dialog instanceof HTMLElement) return dialog;

    const modal = modalTitlebarDragAutoFormBase.getClosest(handleEl, ".modal");
    if (modal instanceof HTMLElement) return modal;

    const parent = handleEl.parentElement;
    return parent instanceof HTMLElement ? parent : null;
}

export function initModalTitlebarDragAuto(root: Document | HTMLElement = document): void {
    const scopeRoot = root instanceof HTMLElement ? root : document;
    const handles = modalTitlebarDragAutoFormBase.getHeaders(scopeRoot);

    for (const handleEl of handles) {
        if (!(handleEl instanceof HTMLElement)) continue;

        const container = resolveDraggableContainer(handleEl);
        if (!(container instanceof HTMLElement)) continue;

        enableModalTitlebarDrag(container, { isEnabled: () => isVisible(container) });
    }
}

export function startModalTitlebarDragObserver(): void {
    if (Reflect.get(globalThis, "__modalTitlebarDragObserverStarted") === true) return;
    Reflect.set(globalThis, "__modalTitlebarDragObserverStarted", true);

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of Array.from(mutation.addedNodes || [])) {
                if (!(node instanceof HTMLElement)) continue;

                if (node.matches?.(ELEMENT_MODAL_HEADER_SELECTOR)) {
                    initModalTitlebarDragAuto(node);
                    continue;
                }

                if (modalTitlebarDragAutoFormBase.hasHeader(node)) {
                    initModalTitlebarDragAuto(node);
                }
            }
        }
    });

    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
    });

    Reflect.set(globalThis, "__modalTitlebarDragObserver", observer);
}
